import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

interface PendingRequest {
  id: string;
  url: string;
  method: string;
  body?: any;
  timestamp: number;
  retries: number;
}

@Injectable({
  providedIn: 'root',
})
export class BackgroundSyncService {
  private dbName = 'SyncQueue';
  private storeName = 'pending-requests';
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void>;
  private syncStatusSubject = new Subject<{ syncing: boolean; pending: number }>();
  public syncStatus$ = this.syncStatusSubject.asObservable();

  constructor() {
    this.initPromise = this.initDB();
    this.registerSyncEvent();
  }

  /**
   * Inicializa la base de datos IndexedDB para la cola de sincronización
   */
  private initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        console.warn('IndexedDB no disponible para BackgroundSync');
        reject(new Error('IndexedDB not available'));
        return;
      }

      const request = indexedDB.open(this.dbName, 1);

      request.onerror = () => {
        console.error('Error al abrir IndexedDB para sync:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('retries', 'retries', { unique: false });
        }
      };
    });
  }

  /**
   * Verifica si el DB está disponible
   */
  private async ensureDB(): Promise<void> {
    if (!this.db) {
      await this.initPromise;
    }
  }

  /**
   * Registra un evento de sincronización en background
   */
  private registerSyncEvent(): void {
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      navigator.serviceWorker.ready.then((registration) => {
        // El SW se encargará de disparar el evento de sync
        console.log('Background Sync ready');
      });
    }
  }

  /**
   * Agrega una solicitud a la cola para sincronizar offline
   */
  async addPendingRequest(url: string, method: string = 'GET', body?: any): Promise<string> {
    try {
      await this.ensureDB();
      if (!this.db) throw new Error('DB no disponible');

      const id = `${Date.now()}-${Math.random()}`;
      const request: PendingRequest = {
        id,
        url,
        method,
        body,
        timestamp: Date.now(),
        retries: 0,
      };

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(this.storeName, 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const dbRequest = store.add(request);

        dbRequest.onsuccess = () => {
          console.log('Solicitud agregada a cola:', id);
          this.updateSyncStatus();
          resolve(id);
        };

        dbRequest.onerror = () => {
          console.error('Error al agregar solicitud a cola:', dbRequest.error);
          reject(dbRequest.error);
        };
      });
    } catch (error) {
      console.error('Error en addPendingRequest:', error);
      throw error;
    }
  }

  /**
   * Obtiene todas las solicitudes pendientes
   */
  async getPendingRequests(): Promise<PendingRequest[]> {
    try {
      await this.ensureDB();
      if (!this.db) return [];

      return new Promise((resolve) => {
        const transaction = this.db!.transaction(this.storeName, 'readonly');
        const store = transaction.objectStore(this.storeName);
        const request = store.getAll();

        request.onsuccess = () => {
          resolve(request.result || []);
        };

        request.onerror = () => {
          console.error('Error al obtener solicitudes pendientes:', request.error);
          resolve([]);
        };
      });
    } catch (error) {
      console.error('Error en getPendingRequests:', error);
      return [];
    }
  }

  /**
   * Elimina una solicitud de la cola
   */
  async removePendingRequest(id: string): Promise<void> {
    try {
      await this.ensureDB();
      if (!this.db) return;

      return new Promise((resolve) => {
        const transaction = this.db!.transaction(this.storeName, 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.delete(id);

        request.onsuccess = () => {
          console.log('Solicitud removida de cola:', id);
          this.updateSyncStatus();
          resolve();
        };

        request.onerror = () => {
          console.error('Error al remover solicitud:', request.error);
          resolve();
        };
      });
    } catch (error) {
      console.warn('Error removiendo solicitud:', error);
    }
  }

  /**
   * Actualiza el contador de reintentos de una solicitud
   */
  async updateRetries(id: string, retries: number): Promise<void> {
    try {
      await this.ensureDB();
      if (!this.db) return;

      return new Promise((resolve) => {
        const transaction = this.db!.transaction(this.storeName, 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const getRequest = store.get(id);

        getRequest.onsuccess = () => {
          const request = getRequest.result as PendingRequest;
          if (request) {
            request.retries = retries;
            const updateRequest = store.put(request);
            updateRequest.onsuccess = () => resolve();
            updateRequest.onerror = () => resolve();
          }
        };

        getRequest.onerror = () => resolve();
      });
    } catch (error) {
      console.warn('Error actualizando reintentos:', error);
    }
  }

  /**
   * Obtiene el número de solicitudes pendientes
   */
  async getPendingCount(): Promise<number> {
    try {
      await this.ensureDB();
      if (!this.db) return 0;

      return new Promise((resolve) => {
        const transaction = this.db!.transaction(this.storeName, 'readonly');
        const store = transaction.objectStore(this.storeName);
        const request = store.count();

        request.onsuccess = () => {
          resolve(request.result);
        };

        request.onerror = () => {
          console.error('Error al contar solicitudes:', request.error);
          resolve(0);
        };
      });
    } catch (error) {
      console.warn('Error obteniendo contador:', error);
      return 0;
    }
  }

  /**
   * Limpia la cola completamente
   */
  async clearQueue(): Promise<void> {
    try {
      await this.ensureDB();
      if (!this.db) return;

      return new Promise((resolve) => {
        const transaction = this.db!.transaction(this.storeName, 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.clear();

        request.onsuccess = () => {
          console.log('Cola de sincronización limpiada');
          this.updateSyncStatus();
          resolve();
        };

        request.onerror = () => {
          console.error('Error al limpiar cola:', request.error);
          resolve();
        };
      });
    } catch (error) {
      console.warn('Error limpiando cola:', error);
    }
  }

  /**
   * Actualiza el estado de sincronización
   */
  private async updateSyncStatus(): Promise<void> {
    const count = await this.getPendingCount();
    this.syncStatusSubject.next({
      syncing: false,
      pending: count,
    });
  }

  /**
   * Notifica cuando se restablece la conexión
   */
  notifyOnline(): void {
    console.log('Conexión restablecida. Sincronizando cola...');
    this.syncStatusSubject.next({
      syncing: true,
      pending: 0,
    });
  }

  /**
   * Notifica cuando se pierde la conexión
   */
  notifyOffline(): void {
    console.log('Conexión perdida. Usando modo offline...');
  }
}
